import React from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useTranslation } from '../../contexts/I18nProvider';
import {
  DATE_RANGE_PRESETS,
  DateRangePreset,
  DateRangeValue,
  dayCount,
  effectiveDays,
  isoWeek,
  periodStart,
  periodStep,
  shiftPeriod,
  validateDateRange
} from '../../utils/dateRange';

interface DateRangeFilterProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  disabled?: boolean;
}

const PRESET_FALLBACK: Record<Exclude<DateRangePreset, 'custom'>, string> = {
  today: 'I dag',
  yesterday: 'I går',
  thisWeek: 'Denne uken',
  lastWeek: 'Forrige uke',
  last7: 'Siste 7 dager',
  last30: 'Siste 30 dager',
  thisMonth: 'Denne måneden',
  lastMonth: 'Forrige måned'
};

const DATE_LOCALES: Record<string, string> = { nb: 'nb-NO', sv: 'sv-SE', en: 'en-GB' };

/**
 * Periodevelger for lister med datofilter: hurtigvalg + Fra/Til (hele dager), med valgfritt
 * klokkeslett. Endringer meldes straks via onChange; forelderen henter data når perioden er
 * gyldig (validateDateRange). Et hurtigvalg regnes fra dagens dato hver gang det brukes.
 * Er perioden en hel uke eller måned, kan den blas med piler (TW 36443153).
 */
export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ value, onChange, disabled = false }) => {
  const { t, language } = useTranslation();
  const { fromDate, toDate } = effectiveDays(value);
  const error = validateDateRange(value);
  const days = dayCount(value);

  // Bla uke-/månedsvis: bare når perioden er en hel uke eller måned
  const step = periodStep(value);
  const start = step ? periodStart(value) : null;
  const previous = step ? shiftPeriod(value, -1) : null;
  const next = step ? shiftPeriod(value, 1) : null;
  let stepLabel = '';
  if (step === 'week' && start) {
    const { week, year } = isoWeek(start);
    stepLabel = t('common.dateRange.week', { '0': week }, `Uke ${week}`)
      + (year !== new Date().getFullYear() ? ` ${year}` : '');
  } else if (step === 'month' && start) {
    const month = start.toLocaleDateString(DATE_LOCALES[language] ?? 'nb-NO', { month: 'long', year: 'numeric' });
    stepLabel = month.charAt(0).toUpperCase() + month.slice(1);
  }

  // Et hurtigvalg betyr hele dager — klokkeslett fra en tidligere egendefinert periode skal ikke henge igjen
  const selectPreset = (preset: DateRangePreset) => {
    onChange({ ...value, preset, includeTime: false });
  };

  // Endring i et datofelt gjør perioden egendefinert, med dagene som vises nå som utgangspunkt
  const setDay = (field: 'fromDate' | 'toDate', day: string) => {
    onChange({ ...value, preset: 'custom', fromDate, toDate, [field]: day });
  };

  const errorText = error === 'fromAfterTo'
    ? t('common.dateRange.errors.fromAfterTo', {}, 'Fra-datoen er etter til-datoen.')
    : error === 'invalidTime'
      ? t('common.dateRange.errors.invalidTime', {}, 'Klokkeslett må være tt:mm.')
      : error === 'missing'
        ? t('common.dateRange.errors.missing', {}, 'Velg både fra- og til-dato.')
        : null;

  const inputClass = 'px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50';

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {/* Hurtigvalg */}
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t('common.dateRange.label', {}, 'Periode')}>
        <Calendar className="w-4 h-4 text-gray-500 mr-1" aria-hidden="true" />
        {DATE_RANGE_PRESETS.map(preset => {
          const active = value.preset === preset;
          return (
            <button
              key={preset}
              type="button"
              disabled={disabled}
              aria-pressed={active}
              onClick={() => selectPreset(preset)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors disabled:opacity-50 ${
                active
                  ? 'bg-teal-600 border-teal-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t(`common.dateRange.presets.${preset}`, {}, PRESET_FALLBACK[preset as Exclude<DateRangePreset, 'custom'>])}
            </button>
          );
        })}
      </div>

      {/* Bla én uke/måned bakover eller fremover */}
      {step && (
        <div className="flex items-center gap-1" role="group" aria-label={t('common.dateRange.browse', {}, 'Bla i perioder')}>
          <button
            type="button"
            disabled={disabled || !previous}
            onClick={() => previous && onChange(previous)}
            aria-label={step === 'week'
              ? t('common.dateRange.previousWeek', {}, 'Forrige uke')
              : t('common.dateRange.previousMonth', {}, 'Forrige måned')}
            className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          </button>
          <span className="min-w-[8rem] text-center text-sm font-medium text-gray-700" aria-live="polite">{stepLabel}</span>
          <button
            type="button"
            disabled={disabled || !next}
            onClick={() => next && onChange(next)}
            aria-label={step === 'week'
              ? t('common.dateRange.nextWeek', {}, 'Neste uke')
              : t('common.dateRange.nextMonth', {}, 'Neste måned')}
            className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Fra – Til */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          {t('common.dateRange.from', {}, 'Fra')}
          <input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            disabled={disabled}
            onChange={e => setDay('fromDate', e.target.value)}
            className={`${inputClass} ${error === 'fromAfterTo' ? 'border-red-400' : 'border-gray-300'}`}
          />
          {value.includeTime && (
            <input
              type="time"
              value={value.fromTime}
              disabled={disabled}
              onChange={e => onChange({ ...value, fromTime: e.target.value })}
              className={`${inputClass} border-gray-300 w-28`}
              aria-label={t('common.dateRange.fromTime', {}, 'Fra klokkeslett')}
            />
          )}
        </label>
        <span className="text-gray-400" aria-hidden="true">–</span>
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          {t('common.dateRange.to', {}, 'Til og med')}
          <input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            disabled={disabled}
            onChange={e => setDay('toDate', e.target.value)}
            className={`${inputClass} ${error === 'fromAfterTo' ? 'border-red-400' : 'border-gray-300'}`}
          />
          {value.includeTime && (
            <input
              type="time"
              value={value.toTime}
              disabled={disabled}
              onChange={e => onChange({ ...value, toTime: e.target.value })}
              className={`${inputClass} border-gray-300 w-28`}
              aria-label={t('common.dateRange.toTime', {}, 'Til klokkeslett')}
            />
          )}
        </label>

        <button
          type="button"
          disabled={disabled}
          aria-pressed={value.includeTime}
          onClick={() => onChange({ ...value, includeTime: !value.includeTime })}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-50 ${
            value.includeTime
              ? 'bg-teal-50 border-teal-400 text-teal-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Clock className="w-4 h-4" aria-hidden="true" />
          {t('common.dateRange.time', {}, 'Klokkeslett')}
        </button>
      </div>

      {/* Status: antall dager eller hva som er galt */}
      <span className={`text-sm ${errorText ? 'text-red-600' : 'text-gray-500'}`} role={errorText ? 'alert' : undefined}>
        {errorText ?? (days === 1
          ? t('common.dateRange.oneDay', {}, '1 dag')
          : t('common.dateRange.days', { '0': days }, `${days} dager`))}
      </span>
    </div>
  );
};
